
import { useRef, useState } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Ride } from '@/lib/types/core';
import { MapPin, Loader2, Compass, LayoutList, Map } from 'lucide-react';
import RidePreviewCard from '@/components/rides/RidePreviewCard';
import { mapboxgl } from './utils/mapboxConfig';
import { useGeolocation } from './hooks/useGeolocation';
import { useMapInitialization } from './hooks/useMapInitialization';
import { useMarkers } from './hooks/useMarkers';
import RideListView from '@/components/ui-custom/RideListView';
import { Point } from '@/lib/types/core';

interface RideMapProps {
  rides: Ride[];
  loading?: boolean;
  defaultCenter: Point;
  defaultZoom?: number;
  showListView?: boolean;
  setPoint?: (point: Point) => void;
}

const RideMap = ({ 
  rides, 
  loading = false, 
  defaultCenter,
  defaultZoom = 3,
  showListView = false,
  setPoint
}: RideMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [selectedRide, setSelectedRide] = useState<Ride | null>(null);
  const [userInteracted, setUserInteracted] = useState(false);
  const userMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map');
  
  // Use our custom hooks
  const { mapLoaded, mapError } = useMapInitialization({
    mapContainer,
    mapRef: map,
    defaultCenter: [defaultCenter.longitude, defaultCenter.latitude],
    defaultZoom,
    onInteraction: () => setUserInteracted(true)
  });
  
  const { markersRef, addMarkers } = useMarkers({
    map,
    mapLoaded,
    rides,
    loading,
    userInteracted,
    selectedRide,
    setSelectedRide
  });
  
  const { isLocating, getUserLocation } = useGeolocation({
    map,
    mapLoaded,
    userMarkerRef,
    addMarkers
  });

  const renderMapModeSelector = (viewMode: 'map' | 'list') => {
    return;
    return (
      <div className="flex justify-end mb-2">
        <div className="flex space-x-2">
          <button
            className={`p-2 rounded-md ${viewMode === 'map' ? 'bg-adv-orange text-white' : 'bg-adv-darkGray text-adv-lightGray hover:bg-adv-darkGray/70'}`}
            onClick={() => setViewMode('map')}
            aria-label="Map View"
            title="Map View"
          >
            <Map size={20} />
          </button>
          <button
            className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-adv-orange text-white' : 'bg-adv-darkGray text-adv-lightGray hover:bg-adv-darkGray/70'}`}
            onClick={() => setViewMode('list')}
            aria-label="List View"
            title="List View"
          >
            <LayoutList size={20} />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      
      {viewMode === 'map' ? (
        <div className="relative w-full h-[500px] md:h-[600px] rounded-lg overflow-hidden">
          {mapError && (
            <div className="absolute inset-0 bg-adv-charcoal flex items-center justify-center p-4 z-10">
              <div className="text-center">
                <MapPin size={32} className="text-adv-orange mb-4 mx-auto" />
                <h3 className="text-lg font-bold text-white mb-2">Map Error</h3>
                <p className="text-adv-lightGray">{mapError}</p>
              </div>
            </div>
          )}
          
          {loading && !mapError && (
            <div className="absolute inset-0 bg-adv-charcoal/70 backdrop-blur-sm flex items-center justify-center z-10">
              <div className="text-center">
                <Loader2 size={32} className="text-adv-orange animate-spin mb-4 mx-auto" />
                <p className="text-white">Loading ride map...</p>
              </div>
            </div>
          )}
          
          <div ref={mapContainer} className="absolute inset-0" />
          
          {/* Geolocation button */}
          {mapLoaded && !mapError && (
            <button
              onClick={getUserLocation}
              disabled={isLocating}
              className="absolute right-14 top-4 bg-adv-charcoal border border-adv-darkGray p-2 rounded-md z-10 hover:bg-adv-darkGray transition-colors"
              title="Center to my location"
            >
              {isLocating ? (
                <Loader2 size={18} className="text-white animate-spin" />
              ) : (
                <Compass size={18} className="text-white" />
              )}
            </button>
          )}
          
          {selectedRide && (
            <div className="absolute bottom-4 left-0 right-0 mx-auto max-w-md px-4 z-20">
              <div className="relative">
                <button 
                  className="absolute -top-2 -right-2 bg-adv-darkGray rounded-full p-1 text-white z-10"
                  onClick={() => setSelectedRide(null)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18 6L6 18"></path>
                    <path d="M6 6l12 12"></path>
                  </svg>
                </button>
                <RidePreviewCard ride={selectedRide} />
              </div>
            </div>
          )}
        </div>
      ) : (
        <RideListView rides={rides} loading={loading} />
      )}
      
      {showListView && viewMode === 'map' && (
        <div className="mt-8">
          <h3 className="heading-sm text-white mb-4">Rides List</h3>
          <RideListView rides={rides} loading={loading} />
        </div>
      )}
    </div>
  );
};

export default RideMap;
